import { Download, Filter, Search } from "lucide-react";
import { useMemo, useRef, useState, type ReactNode } from "react";
import {
  DEFAULT_PARTY_FILTERS,
  filterAndSortParties,
  type PartyFilters,
} from "../trips/selectors";
import { AGEING_BUCKETS, type PartyOutstanding } from "../trips/types";
import { CompactSelect, type SelectOption } from "../shared/CompactSelect";
import { FilterPanel } from "../shared/FilterPanel";
import { formatCurrency } from "../shared/currency";
import { TablePager } from "../shared/TablePager";
import { usePagedRows } from "../shared/usePagedRows";

type PartiesPageProps = {
  partyOutstanding: PartyOutstanding[];
  onExportPartyStatement: (partyName: string) => void;
};

const ageingOptions: SelectOption<PartyFilters["ageingBucket"]>[] = [
  { value: "all", label: "All ages" },
  ...AGEING_BUCKETS.map((bucket) => ({
    value: bucket,
    label: `${bucket} days`,
  })),
];

const sortOptions: SelectOption<PartyFilters["sort"]>[] = [
  { value: "outstanding", label: "Highest outstanding" },
  { value: "oldest", label: "Oldest balance" },
  { value: "trips", label: "Most trips" },
  { value: "name", label: "Party name" },
];

export function PartiesPage({
  partyOutstanding,
  onExportPartyStatement,
}: PartiesPageProps) {
  const [filters, setFilters] = useState<PartyFilters>(DEFAULT_PARTY_FILTERS);
  const [draftFilters, setDraftFilters] =
    useState<PartyFilters>(DEFAULT_PARTY_FILTERS);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterTriggerRef = useRef<HTMLButtonElement>(null);
  const filteredParties = useMemo(
    () => filterAndSortParties(partyOutstanding, filters),
    [filters, partyOutstanding]
  );
  const { page, pageCount, pageRows, nextPage, previousPage } =
    usePagedRows(filteredParties);
  const activeFilterCount = [
    filters.ageingBucket !== "all",
    filters.sort !== "outstanding",
  ].filter(Boolean).length;

  const openFilters = () => {
    setDraftFilters(filters);
    setIsFilterOpen(true);
  };

  return (
    <section className="orbit-card overflow-hidden">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 p-3">
        <div>
          <h2 className="font-semibold text-slate-950">Party balances</h2>
          <p className="hidden text-xs text-slate-500 sm:block">
            One receivable line per customer.
          </p>
        </div>
        <div className="flex min-w-0 flex-1 items-center justify-end gap-2 sm:flex-none">
          <label className="relative min-w-0 flex-1 sm:w-64">
            <Search
              size={16}
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
            />
            <span className="sr-only">Search parties</span>
            <input
              value={filters.query}
              onChange={(event) =>
                setFilters((current) => ({
                  ...current,
                  query: event.target.value,
                }))
              }
              placeholder="Search party"
              className="h-11 w-full rounded-xl border pl-9 pr-3 text-base outline-none sm:h-9 sm:text-sm"
            />
          </label>
          <button
            ref={filterTriggerRef}
            type="button"
            onClick={openFilters}
            className="orbit-tab inline-flex h-11 shrink-0 items-center gap-2 rounded-xl border border-slate-200 px-3 text-sm font-semibold sm:h-9"
          >
            <Filter size={16} />
            <span className="hidden sm:inline">Filter</span>
            {activeFilterCount > 0 ? (
              <span className="orbit-tone-amber rounded-full px-1.5 py-0.5 text-[10px]">
                {activeFilterCount}
              </span>
            ) : null}
          </button>
        </div>
      </div>

      {partyOutstanding.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-slate-500">
          No party has an open balance right now.
        </div>
      ) : filteredParties.length === 0 ? (
        <div className="px-4 py-8 text-center text-sm text-slate-500">
          No party matches these filters.
        </div>
      ) : (
        <>
          <div className="divide-y divide-slate-100 lg:hidden">
            {pageRows.map((party) => (
              <article key={party.partyName} className="px-3 py-2.5">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold text-slate-950">
                      {party.partyName}
                    </p>
                    <p className="text-xs text-slate-500">
                      {party.trips} trips · oldest {party.ageDays} days
                    </p>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <p className="text-right text-sm font-bold text-amber-700">
                      {formatCurrency(party.outstanding)}
                    </p>
                    <button
                      type="button"
                      onClick={() => onExportPartyStatement(party.partyName)}
                      aria-label={`Download statement for ${party.partyName}`}
                      title="Download statement"
                      className="orbit-tab grid h-11 w-11 place-items-center rounded-xl border border-slate-200"
                    >
                      <Download size={17} />
                    </button>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between text-xs text-slate-500">
                  <span>{party.ageingBucket} days</span>
                  <span>Received {formatCurrency(party.received)}</span>
                </div>
              </article>
            ))}
          </div>
          <div className="hidden lg:block">
            <table className="w-full table-fixed text-left text-sm">
              <thead className="text-xs uppercase text-slate-500">
                <tr>
                  <th className="w-[27%] px-3 py-2.5">Party</th>
                  <th className="w-[10%] px-3 py-2.5 text-center">Trips</th>
                  <th className="w-[16%] px-3 py-2.5">Ageing</th>
                  <th className="w-[18%] px-3 py-2.5 text-right">Received</th>
                  <th className="w-[19%] px-3 py-2.5 text-right">Outstanding</th>
                  <th className="w-[10%] px-3 py-2.5 text-right">Statement</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageRows.map((party) => (
                  <tr key={party.partyName}>
                    <td className="px-3 py-2.5">
                      <p className="truncate font-semibold text-slate-950">
                        {party.partyName}
                      </p>
                      <p className="text-xs text-slate-500">
                        Oldest open {party.ageDays} days
                      </p>
                    </td>
                    <td className="px-3 py-2.5 text-center">{party.trips}</td>
                    <td className="px-3 py-2.5">
                      <span className="orbit-tone-amber rounded-full px-2 py-1 text-xs font-semibold">
                        {party.ageingBucket} days
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      {formatCurrency(party.received)}
                    </td>
                    <td className="px-3 py-2.5 text-right font-bold text-amber-700">
                      {formatCurrency(party.outstanding)}
                    </td>
                    <td className="px-3 py-2.5 text-right">
                      <button
                        type="button"
                        onClick={() => onExportPartyStatement(party.partyName)}
                        aria-label={`Download statement for ${party.partyName}`}
                        title="Download statement"
                        className="orbit-tab inline-grid h-9 w-9 place-items-center rounded-lg border border-slate-200"
                      >
                        <Download size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <TablePager
            page={page}
            pageCount={pageCount}
            totalRows={filteredParties.length}
            onPrevious={previousPage}
            onNext={nextPage}
          />
        </>
      )}

      <FilterPanel
        open={isFilterOpen}
        title="Party filters"
        triggerRef={filterTriggerRef}
        onClose={() => setIsFilterOpen(false)}
        onReset={() => setDraftFilters(DEFAULT_PARTY_FILTERS)}
        onApply={() => {
          setFilters((current) => ({ ...draftFilters, query: current.query }));
          setIsFilterOpen(false);
        }}
      >
        <FilterField label="Ageing">
          <CompactSelect
            ariaLabel="Party ageing filter"
            value={draftFilters.ageingBucket}
            options={ageingOptions}
            onChange={(ageingBucket) =>
              setDraftFilters((current) => ({ ...current, ageingBucket }))
            }
          />
        </FilterField>
        <FilterField label="Order">
          <CompactSelect
            ariaLabel="Party sort order"
            value={draftFilters.sort}
            options={sortOptions}
            onChange={(sort) =>
              setDraftFilters((current) => ({ ...current, sort }))
            }
          />
        </FilterField>
      </FilterPanel>
    </section>
  );
}

function FilterField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <p className="mb-1.5 text-xs font-bold uppercase text-slate-500">{label}</p>
      {children}
    </div>
  );
}
